import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { guidelinesApi, GuidelineDocDetail as GuidelineDoc } from "../../api";
import GuidelineDetailPage from "./GuidelineDetailPage";
import EditGuidelineDialog from "./EditGuidelineDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const GuidelineDetailContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guideline, setGuideline] = useState<GuidelineDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchGuideline = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await guidelinesApi.get(id);
      setGuideline(response.data);
    } catch (err) {
      console.error("Failed to fetch guideline:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGuideline();
  }, [fetchGuideline]);

  const handleEditSuccess = () => {
    setSuccessMessage("Guideline updated successfully");
    fetchGuideline(); // Refresh data
  };

  const handleDeleteSuccess = () => {
    setSuccessMessage("Guideline deleted successfully");
    setTimeout(() => {
      navigate("/guidelines");
    }, 1000);
  };

  return (
    <>
      <GuidelineDetailPage
        guideline={guideline}
        loading={loading}
        onBack={() => navigate("/guidelines")}
        onEdit={() => setEditDialogOpen(true)}
        onDelete={() => setDeleteDialogOpen(true)}
        onRefresh={fetchGuideline}
        onChunkUpdated={fetchGuideline}
      />

      {guideline && (
        <>
          <EditGuidelineDialog
            open={editDialogOpen}
            guideline={guideline}
            onClose={() => setEditDialogOpen(false)}
            onSuccess={handleEditSuccess}
          />
          <DeleteConfirmDialog
            open={deleteDialogOpen}
            guidelineId={guideline.guideline_docs_id}
            guidelineName={guideline.title}
            onClose={() => setDeleteDialogOpen(false)}
            onSuccess={handleDeleteSuccess}
          />
        </>
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GuidelineDetailContainer;

